import { useGlobal } from "@/app/providers/use-global";
import { postActivityMessageToParent } from "@/lib/utils/crossframe";
import { type UserPromptLocalStorage, ls } from "@/lib/utils/ls";
import { isGptOssModel } from "@/shared/data/hardcoded-shared-settings";
import type { Model } from "@/shared/data/models";
import { BASE_API_URL } from "@/shared/env";
import { COMPOUND_BETA_OSS_MODEL_ID } from "@/shared/lib/compound-oss-hack";
import { isSpecialParamsModel } from "@/shared/lib/utils";
import { useUser } from "@/shared/providers/user-provider";
import Groq from "groq-sdk";
import type { Stream } from "groq-sdk/lib/streaming.mjs";
import type { ChatCompletionChunk } from "groq-sdk/resources/chat/completions";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useState,
} from "react";
import { toast } from "sonner";

type Message = Groq.Chat.Completions.ChatCompletionMessageParam;

const DEFAULT_COMPLETIONS_ERROR_MESSAGE =
  "Something went wrong. Please, try again.";

export const groq = new Groq({
  baseURL: BASE_API_URL,
  // baseURL: "http://localhost:4000/api/mock",
  dangerouslyAllowBrowser: true,
  apiKey: "My Groq API Key",
});

export interface ChatResponseStats {
  prompt_tokens: number;
  prompt_time: number;
  completion_tokens: number;
  completion_time: number;
  total_tokens: number;
  total_time: number;
}

// Define the structure for a tool call
export interface ToolCall {
  index: number;
  type: string;
  arguments: string;
  output?: string;
  isOutputVisible?: boolean; // For UI state
}

// Define additional fields that might be in delta chunks
interface ReasoningDelta {
  reasoning: string;
  isPlaying: boolean;
}

interface ToolCallsDelta {
  executed_tools: Array<{
    index: number;
    type: string;
    arguments: string;
    output?: string;
  }>;
}

export interface PromptAndResponse {
  user_prompt: string;
  assistant_response: string;
  reasoning?: string; // Added for reasoning steps
  tool_calls?: ToolCall[]; // Added for tool calls
  edited_response: string;
  edited_stats: ChatResponseStats | null;
  response_duration: string | null;
  edited_response_duration: string | null;
  stats: ChatResponseStats | null;
  model: Model | null | undefined;
}

export interface MessageParams {
  modifeSystemPrompt?: string;
  chatHistory: PromptAndResponse[];
  setChatHistory: Dispatch<SetStateAction<PromptAndResponse[]>>;
  user_prompt: string;
  isEdit: boolean;
  setInputBuffer: Dispatch<SetStateAction<string>>;
  scrollChatListBottom: RefObject<HTMLDivElement>; // Added for scrolling
  setIsLoadingMessage: Dispatch<SetStateAction<boolean>>; // Added to manage loading state
}

// TODO: once anon stych jwt is supported, setAnonymousPromptsUsedCount => prev+1 if user is not logged in
export const useSendMessage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { buildRequestHeaders } = useUser();
  const {
    currentModel,
    setShowLoginCardInHome,
    systemPrompt,
    isUserLoggedIn,
    isProfileLoading,
    autoScrollBack, // Added for scroll control
  } = useGlobal();

  const sendMessage = async ({
    modifeSystemPrompt,
    user_prompt,
    chatHistory,
    setChatHistory,
    isEdit,
    setInputBuffer,
    scrollChatListBottom, // Added
    setIsLoadingMessage, // Added
  }: MessageParams): Promise<undefined | { error: string }> => {
    if (!(isUserLoggedIn || isProfileLoading)) {
      const userPrompt: UserPromptLocalStorage = {
        text: user_prompt,
        timestamp: Date.now(),
      };
      ls.set("unauthenticatedUserPrompt", userPrompt);
      postActivityMessageToParent();
      setShowLoginCardInHome(true);
      // biome-ignore lint/suspicious/noConsole: <explanation>
      console.warn("No user logged in");
      // we can't get a response, remove user prompt from chat history
      setChatHistory((prev) => prev.slice(0, -1));
      setInputBuffer(user_prompt);
      return { error: "No user logged in" };
    }

    if (!currentModel) {
      // biome-ignore lint/suspicious/noConsole: <explanation>
      console.warn("No current model");
      // we can't get a response, remove user prompt from chat history
      setChatHistory((prev) => prev.slice(0, -1));
      setInputBuffer(user_prompt);
      return { error: "No current model" };
    }

    setInputBuffer("");
    setIsProcessing(true);
    // Set loading message state immediately before starting async operations
    setIsLoadingMessage(true); // Set initial loading state
    // Expand chat if embedded in groq.com
    postActivityMessageToParent();

    const messages: {
      content: string;
      role: "user" | "assistant" | "system";
    }[] = [];

    // --- Message History Construction (no changes needed here) ---
    if (modifeSystemPrompt) {
      messages.push({ content: modifeSystemPrompt, role: "system" });
    } else {
      // Use original system prompt if not an edit with modifier
      // For edits, user_prompt is the original assistant response, so no system prompt maybe?
      // Let's keep the original logic for now, assuming edits might still benefit from base system prompt.
      // If `isEdit` is true and `modifeSystemPrompt` is set, that one is used.
      // If `isEdit` is true and `modifeSystemPrompt` is NOT set (shouldn't happen with current dropdown), base system prompt is used.
      messages.push({
        content: systemPrompt,
        role: "system",
      });
    }
    // If it's an edit, chatHistory might be empty or used differently based on API needs.
    // The current dropdown passes [], and puts original response in user_prompt.
    // If not an edit, add full history.
    if (!isEdit) {
      chatHistory.forEach((pair) => {
        messages.push({ content: pair.user_prompt, role: "user" });

        // Include edited response if available, otherwise original
        const assistantContent =
          pair.edited_response || pair.assistant_response;
        messages.push({ content: assistantContent, role: "assistant" });
      });
    }
    messages.push({ content: user_prompt, role: "user" });

    // --- Header Construction (no changes needed here) ---
    const headers = await buildRequestHeaders();
    const headersWithApp = {
      ...headers,
      "x-groq-keep-alive-pings": "true",
      "Groq-App": "chat",
    } as Record<string, string>;

    // HACK: Handle compound beta oss
    if (currentModel.id === COMPOUND_BETA_OSS_MODEL_ID) {
      headersWithApp["Groq-Model-Version"] = "oss";
    }
    // end HACK

    const partner = localStorage.getItem("groq-partner");
    if (partner) {
      headersWithApp["Groq-Partner"] = partner;
    }

    try {
      // --- API Call Parameters (no changes needed here) ---
      let temp = 0.1;
      let topP = 1;
      let maxCompletionTokens = 2048;
      if (isSpecialParamsModel(currentModel.id)) {
        temp = 0.6;
        topP = 0.95;
        maxCompletionTokens = 8192;
      }

      if (isGptOssModel(currentModel.id)) {
        maxCompletionTokens = 16384;
        temp = 1;
      }

      const resp: Stream<ChatCompletionChunk> =
        await groq.chat.completions.create(
          {
            model:
              currentModel.id === COMPOUND_BETA_OSS_MODEL_ID // HACK: remove condition for oss hack
                ? "compound-beta"
                : currentModel.id,
            messages: processMessages(messages, currentModel.id),
            temperature: temp,
            max_completion_tokens: Math.min(
              maxCompletionTokens,
              currentModel.context_window,
            ),
            top_p: topP,
            stream: true,
          },
          {
            headers: headersWithApp,
            maxRetries: 0,
          },
        );

      // --- Process Stream ---
      for await (const chunk of resp) {
        const delta = chunk.choices[0]?.delta;
        const finishReason = chunk.choices[0]?.finish_reason;
        const usage = chunk.x_groq?.usage;
        const chunkError = chunk?.x_groq?.error;

        // Handle errors first
        if (chunkError) {
          let errorMessage = "";
          switch (chunkError) {
            case "over_capacity":
              errorMessage = `Sorry, ${chunk.model} is currently experiencing heavy demand. Please try a different model.`;
              break;
            case "rate_limit_exceeded":
              errorMessage =
                "Woah, you're keeping us busy. You have reached your maximum number of requests per minute. Take a quick stretch break then try again.";
              break;
            case "service_unavailable":
              errorMessage = "Service is currently unavailable.";
              break;
            default:
              errorMessage = DEFAULT_COMPLETIONS_ERROR_MESSAGE;
          }
          setIsProcessing(false);
          setIsLoadingMessage(false); // Clear loading state on error
          toast.error(errorMessage);
          setInputBuffer(user_prompt); // Restore input buffer
          // Remove the optimistic chat entry added before the call
          setChatHistory((prev) => prev.slice(0, -1));
          return { error: errorMessage }; // Return error indication
        }

        let shouldScroll = false; // Flag to trigger scroll after state update

        setChatHistory((prev) => {
          if (prev.length === 0) {
            return prev;
          }

          const lastItemIndex = prev.length - 1;
          // Important: Create a deep enough copy if nested objects/arrays are modified directly
          // Shallow copy works if we replace arrays/objects entirely like below
          const lastItem = { ...prev[lastItemIndex] };

          let updated = false;

          // Append content
          if (delta?.content) {
            if (isEdit) {
              lastItem.edited_response =
                (lastItem.edited_response || "") + delta.content;
            } else {
              lastItem.assistant_response =
                (lastItem.assistant_response || "") + delta.content;
            }
            updated = true;
            shouldScroll = true;
          }

          // Append reasoning
          const reasoningDelta = delta as unknown as ReasoningDelta;
          if (!isEdit && reasoningDelta?.reasoning) {
            lastItem.reasoning =
              (lastItem.reasoning || "") + reasoningDelta.reasoning;
            updated = true;
            shouldScroll = true;
          }

          // Handle tool calls
          const toolCallsDelta = delta as unknown as ToolCallsDelta;
          if (
            !isEdit &&
            toolCallsDelta?.executed_tools &&
            Array.isArray(toolCallsDelta.executed_tools)
          ) {
            // Ensure array exists, create a new array reference for state update
            const currentToolCalls = lastItem.tool_calls
              ? [...lastItem.tool_calls]
              : [];
            let toolsUpdated = false;

            toolCallsDelta.executed_tools.forEach((toolDelta) => {
              const existingToolIndex = currentToolCalls.findIndex(
                (tc) => tc.index === toolDelta.index,
              );
              if (existingToolIndex !== -1) {
                // Update existing tool call: Create new object for the update
                currentToolCalls[existingToolIndex] = {
                  ...currentToolCalls[existingToolIndex],
                  ...toolDelta, // Merge new data (like output)
                };
                toolsUpdated = true;
              } else {
                // Add new tool call
                currentToolCalls.push({ ...toolDelta, isOutputVisible: false }); // Add with default visibility
                toolsUpdated = true;
              }
            });

            if (toolsUpdated) {
              lastItem.tool_calls = currentToolCalls; // Assign the new array
              updated = true;
              shouldScroll = true;
            }
          }

          // Handle stats on finish
          if (finishReason === "stop" || finishReason === "length") {
            if (usage) {
              const statsWithRightType = {
                prompt_tokens: usage.prompt_tokens ?? 0,
                prompt_time: usage.prompt_time ?? 0,
                completion_tokens: usage.completion_tokens ?? 0,
                completion_time: usage.completion_time ?? 0,
                total_tokens: usage.total_tokens ?? 0,
                total_time: usage.total_time ?? 0,
              };

              if (isEdit) {
                lastItem.edited_stats = statsWithRightType;
              } else {
                lastItem.stats = statsWithRightType;
              }
              updated = true;
            }
          }

          if (!updated) {
            return prev; // No relevant delta, return previous state
          }

          // Create new array with updated item
          const newChat = [...prev.slice(0, lastItemIndex), lastItem];
          return newChat;
        });

        // Trigger scroll after state update if needed
        if (shouldScroll && autoScrollBack && scrollChatListBottom.current) {
          requestAnimationFrame(() => {
            scrollChatListBottom.current?.scrollIntoView(false);
          });
        }

        // Handle finish reasons (stop/length)
        if (finishReason === "stop" || finishReason === "length") {
          if (finishReason === "length") {
            toast.error(
              "Context Limit or Max Tokens reached. Please clear chat and start a new conversation.",
            );
          }
          // Final state update handled above, just update processing/loading flags
          setIsProcessing(false);
          setIsLoadingMessage(false); // Clear loading message state on finish
          break; // Exit loop
        }
      }

      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    } catch (error: any) {
      // console.error("Error sending message:", error); // Log for debugging
      setIsProcessing(false);
      setIsLoadingMessage(false); // Clear loading state on error
      setInputBuffer(user_prompt); // Restore input buffer
      const errorMessage =
        error?.error?.error?.message ||
        error?.message ||
        "An unexpected error occurred.";
      if (error?.status && error?.status === 429) {
        toast.error(errorMessage);
      } else {
        toast.error(DEFAULT_COMPLETIONS_ERROR_MESSAGE); // Use default for non-rate limit errors
      }
      // Remove the optimistic chat entry added before the call
      setChatHistory((prev) => prev.slice(0, -1));
      return { error: errorMessage }; // Indicate error
    } finally {
      // Ensure these flags are reset regardless of success or error path
      setIsProcessing(false);
      setIsLoadingMessage(false);
    }
    // Removed finally block that just set isProcessing = false, as it's handled on success/error paths now.
  };

  // Expose the necessary state and functions
  return {
    sendMessage,
    isProcessing, // Still useful to disable input etc.
    setIsProcessing, // Keep if needed elsewhere
  };
};

// --- processMessages function remains unchanged ---
function processMessages(messages: Message[], modelId: string): Message[] {
  const nonEmptyMessages = messages.filter((m) => m.content !== "");

  // Remove system message for special models
  const cleanMessages = isSpecialParamsModel(modelId) // Filter out system messages for special params models
    ? nonEmptyMessages.filter((m) => m.role !== "system")
    : nonEmptyMessages;

  // Add system message for qwq models
  if (modelId.includes("qwq")) {
    cleanMessages.unshift({
      content:
        "Be concise, don't use any Chinese characters in your response unless specifically directed to do so.",
      role: "system",
    });
  }

  // Handle compound models by prepending system prompt to first user message
  if (modelId.toLowerCase().includes("compound")) {
    const systemMessage = cleanMessages.find((m) => m.role === "system");
    if (systemMessage) {
      const firstUserMessage = cleanMessages.find((m) => m.role === "user");
      if (firstUserMessage) {
        firstUserMessage.content = `${systemMessage.content}\n${firstUserMessage.content}`;
      }
      return cleanMessages.filter((m) => m.role !== "system");
    }
  }

  return cleanMessages;
}
